/**
 * The schedule, and where it comes from.
 *
 * One rule outranks everything else in this file: **there is always a document**.
 * A stale schedule rings correct bells nine days out of ten; no schedule rings
 * nothing, ever. So `doc` is never null, the cache never expires on a timer,
 * and a failed fetch is a shrug rather than an error state.
 *
 * The order of evidence, best first:
 *
 *   network  — polled every minute; also our main clock correction path
 *   cache    — the last document this tablet actually saw, read synchronously
 *              at boot so the first paint already has a schedule
 *   seed     — compiled into the bundle, so a tablet that has never once
 *              reached the Worker still knows when chapel starts
 *
 * The Wi-Fi at St. Lucas drops for hours and sits behind a captive portal.
 * `online` tracks that honestly for the kiosk's status dot, and recovery is
 * silent — no retries counter on the wall, no error screen, no teacher having
 * to do anything.
 */

import seedJson from '../shared/seed.json';
import type { ScheduleDoc } from '../shared/types';
import type { Clock } from './clock';

const SCHEDULE_URL = '/api/schedule';
const STORAGE_KEY = 'valor.schedule.v1';

/**
 * The spec: a published change reaches every tablet inside sixty seconds. The
 * jitter is subtracted, never added, so the interval is never longer than the
 * promise — and eleven tablets in two wings stop hitting the Worker in the same
 * millisecond.
 */
const POLL_MIN_MS = 52_000;
const POLL_JITTER_MS = 8_000;

/** After a failure, come back sooner: the sooner we are back, the sooner the clock is. */
const RETRY_MIN_MS = 15_000;
const RETRY_JITTER_MS = 10_000;

/** A captive portal can accept a connection and then never answer. */
const FETCH_TIMEOUT_MS = 10_000;

/** The floor between wake-up fetches, however often the tablet wakes. */
const MIN_NUDGE_GAP_MS = 10_000;

export type Source = 'seed' | 'cache' | 'network';

export interface Store {
  /** Never null. */
  readonly doc: ScheduleDoc;
  readonly source: Source;
  readonly online: boolean;
  readonly lastFetchOk: number | null;
  start(): void;
  refresh(): Promise<boolean>;
  onChange(cb: (doc: ScheduleDoc) => void): void;
}

const SEED = seedJson as unknown as ScheduleDoc;

/**
 * Enough of a shape check that a captive portal's login page, a truncated
 * write, or a half-parsed body can never become "the schedule".
 */
function isScheduleDoc(value: unknown): value is ScheduleDoc {
  if (!value || typeof value !== 'object') return false;
  const doc = value as Partial<ScheduleDoc>;
  if (typeof doc.version !== 'number' || !Number.isFinite(doc.version)) return false;
  if (!Array.isArray(doc.schoolDays)) return false;
  if (!doc.grids || typeof doc.grids !== 'object') return false;
  // Bell groups are created by the office, so there is no fixed set to check
  // for. What must hold is that there is at least one, and that every one of
  // them has real bands — a grid without bands would parse and then ring
  // nothing, which is the failure this guard exists to prevent.
  const grids = Object.values(doc.grids);
  if (grids.length === 0) return false;
  for (const grid of grids) {
    if (!grid || !Array.isArray(grid.bands)) return false;
  }
  if (!doc.overrides || typeof doc.overrides !== 'object') return false;
  if (!Array.isArray(doc.teachers)) return false;
  return true;
}

interface Cached {
  doc: ScheduleDoc;
  /** Corrected epoch ms when this was written. Diagnostic only — never a TTL. */
  savedAt: number;
}

/**
 * Synchronous on purpose. IndexedDB would make the first paint wait on a
 * promise, and the first paint is exactly when a teacher is looking at the
 * tablet wondering whether the bells are working. A schedule document is a few
 * kilobytes; localStorage holds a term of them with room to spare.
 */
function readCache(): ScheduleDoc | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Cached>;
    return isScheduleDoc(parsed?.doc) ? parsed.doc : null;
  } catch {
    return null;
  }
}

function writeCache(doc: ScheduleDoc, savedAt: number): void {
  try {
    const record: Cached = { doc, savedAt };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch (err) {
    // Out of quota or storage disabled. We keep serving from memory; the cost
    // is only that the next reboot falls back to the seed.
    console.warn('[store] could not cache the schedule', err);
  }
}

export function createStore(clock: Clock): Store {
  // Boot from the best thing on disk before anything async happens. A cached
  // document beats the seed unless the seed is genuinely newer — which happens
  // when a tablet has been offline across a redeploy.
  const cached = readCache();
  let doc: ScheduleDoc = cached && cached.version >= SEED.version ? cached : SEED;
  let source: Source = doc === cached ? 'cache' : 'seed';

  let online = true;
  let lastFetchOk: number | null = null;
  let started = false;
  let inFlight: Promise<boolean> | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  /** Monotonic — an Android tablet's own clock is not fit to rate-limit with. */
  let lastAttemptMono = -Infinity;

  const listeners: ((doc: ScheduleDoc) => void)[] = [];

  console.info(`[store] booted from ${source} (schedule v${doc.version})`);

  function announce(): void {
    for (const cb of listeners) {
      try {
        cb(doc);
      } catch (err) {
        console.error('[store] a change listener threw', err);
      }
    }
  }

  function schedule(delayMs: number): void {
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      void tick();
    }, delayMs);
  }

  async function tick(): Promise<void> {
    const ok = await refresh();
    schedule(
      ok
        ? POLL_MIN_MS + Math.random() * POLL_JITTER_MS
        : RETRY_MIN_MS + Math.random() * RETRY_JITTER_MS,
    );
  }

  /**
   * Come back sooner than the poll interval, without letting a tablet that is
   * flapping between visible and hidden — a real thing on Android — turn into a
   * request every quarter second, times eleven.
   */
  function nudge(): void {
    const since = performance.now() - lastAttemptMono;
    schedule(since < MIN_NUDGE_GAP_MS ? MIN_NUDGE_GAP_MS - since : 250);
  }

  async function fetchOnce(): Promise<boolean> {
    lastAttemptMono = performance.now();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const res = await fetch(SCHEDULE_URL, {
        method: 'GET',
        // Belt and braces: the service worker refuses to cache /api/*, and so
        // does the HTTP cache. A cached Date header would poison the clock.
        cache: 'no-store',
        credentials: 'omit',
        headers: { accept: 'application/json' },
        signal: controller.signal,
      });

      if (!res.ok) {
        console.warn(`[store] /api/schedule answered ${res.status}`);
        return false;
      }

      const body: unknown = await res.json();
      const payload = body as { doc?: unknown; serverTime?: unknown };
      if (!isScheduleDoc(payload?.doc)) {
        // Valid JSON that is not our document — a portal, a proxy, a rewrite.
        // Nothing here is trustworthy, least of all its Date header.
        console.warn('[store] /api/schedule did not return a schedule document');
        return false;
      }

      // Only now, having established that this really was our Worker: hand the
      // timestamps to the clock. This is the main clock-correction path — sixty
      // corrections an hour, for free, on top of the schedule poll.
      clock.adopt(
        res.headers.get('date'),
        typeof payload.serverTime === 'number' ? payload.serverTime : undefined,
      );

      const fresh = payload.doc;
      const changed = fresh.version !== doc.version;
      doc = fresh;
      source = 'network';
      lastFetchOk = clock.now();
      writeCache(fresh, lastFetchOk);

      if (changed) {
        console.info(`[store] schedule v${fresh.version} adopted`);
        // Only a real version change reaches the kiosk. Re-rendering on every
        // poll would churn the screen sixty times an hour for nothing.
        announce();
      }
      return true;
    } finally {
      clearTimeout(timeout);
    }
  }

  async function refresh(): Promise<boolean> {
    if (inFlight) return inFlight;
    inFlight = (async () => {
      try {
        const ok = await fetchOnce();
        if (ok !== online) console.info(`[store] network ${ok ? 'back' : 'lost'}`);
        online = ok;
        if (!ok && clock.syncedAt === null) {
          // We have never known the time, on any run. That is the emergency
          // case — a tablet that would ring 2017's bells. /api/time is far
          // cheaper than the schedule, so try it before giving up on the cycle.
          await clock.sync();
        }
        return ok;
      } catch (err) {
        // Offline, captive portal, DNS, abort — all the same to us. Keep the
        // document we have and try again shortly.
        if (online) console.info('[store] network lost', err);
        online = false;
        return false;
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  }

  function start(): void {
    if (started) return;
    started = true;

    void tick();

    // The tablet wakes, or the Wi-Fi comes back: do not sit out the rest of the
    // poll interval. Both of these are cheap and both are common here.
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        console.info('[store] the browser reports the network is back');
        nudge();
      });
      window.addEventListener('offline', () => {
        online = false;
      });
      document.addEventListener('visibilitychange', () => {
        // Coming back from background: the monotonic clock is still sound, but
        // a document may have been published while we were asleep.
        if (document.visibilityState === 'visible') nudge();
      });
    }
  }

  return {
    get doc() {
      return doc;
    },
    get source() {
      return source;
    },
    get online() {
      return online;
    },
    get lastFetchOk() {
      return lastFetchOk;
    },
    start,
    refresh,
    onChange(cb) {
      listeners.push(cb);
    },
  };
}
